import React, { useCallback, useEffect, useMemo, useState } from "react";

import { FoodDetailPanel } from "./FoodDetailPanel";
import { LabelLink } from "./LabelLink";
import { hasBlockingConcern } from "./concerns";
import {
  Badge,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  StatusChip,
  foodsChanged,
} from "./shared";
import { FoodLabelLink, IngestReviewItem, PassioFood } from "./types";
import {
  CatalogApi,
  CatalogChangeRequest,
  CatalogWriteResult,
  ContributionsFilter,
  catalogEditsNeedReview,
  extractApiError,
  extractIngestResult,
  isCatalogStaff,
  isOwnAddition,
} from "./useCatalogApi";
import { useLabelSuggestions } from "./useLabelSuggestions";

interface ContributionsTabProps {
  api: CatalogApi;
  ingestChainId: string;
  customerName?: string;
}

const PAGE_SIZE = 25;

/** Product lists come from the catalog; review lists from the review queue. */
type TabFilter = ContributionsFilter | "pending" | "rejected";

const isRequestFilter = (f: TabFilter): f is "pending" | "rejected" =>
  f === "pending" || f === "rejected";

const FILTERS: Array<{ id: TabFilter; label: string; hint: string }> = [
  { id: "all", label: "All", hint: "Everything you've added or edited" },
  { id: "added", label: "Added", hint: "New products you created" },
  { id: "edited", label: "Edited", hint: "Existing products you changed" },
  {
    id: "pending",
    label: "In review",
    hint: "Edits waiting for Passio to approve",
  },
  {
    id: "rejected",
    label: "Not approved",
    hint: "Edits Passio didn't approve, with the reason",
  },
];

const EMPTY_MESSAGES: Record<TabFilter, string> = {
  all: "You haven't added or edited any catalog products yet.",
  added: "No added products yet.",
  edited: "No edited products yet.",
  pending: "Nothing waiting for review.",
  rejected: "None of your edits were turned down.",
};

/** Short label for a food row. */
const rowSubtitle = (f: PassioFood): string => {
  const parts: string[] = [];
  if (f.brandName) parts.push(f.brandName);
  const upc = f.branded?.productCode;
  if (upc) parts.push(`UPC ${upc}`);
  return parts.join(" · ") || "—";
};

const formatDate = (v?: string): string => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString();
};

const REVIEW_MESSAGE =
  "Sent to Passio for review. The live product stays as it is until your change is approved — you'll find it under In review.";

const ContributionsTab: React.FC<ContributionsTabProps> = ({
  api,
  ingestChainId,
  customerName,
}) => {
  const [filter, setFilter] = useState<TabFilter>("all");
  const [items, setItems] = useState<PassioFood[]>([]);
  const [requests, setRequests] = useState<CatalogChangeRequest[]>([]);
  const [pendingByFood, setPendingByFood] = useState<
    Record<string, CatalogChangeRequest>
  >({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [withdrawing, setWithdrawing] = useState("");

  // Thumbnails: product image (fallback brand image) per row, keyed by food id.
  const [imageMap, setImageMap] = useState<Record<string, string>>({});

  const [selectedId, setSelectedId] = useState("");
  const [selected, setSelected] = useState<PassioFood | null>(null);
  const [draft, setDraft] = useState<PassioFood | null>(null);
  const labelSuggestions = useLabelSuggestions(api, draft);
  const [dirtySinceRecheck, setDirtySinceRecheck] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saveErr, setSaveErr] = useState("");
  // Blocking concern codes on the current draft (from the last validation).
  const [hasBlocking, setHasBlocking] = useState(false);

  const selectedOwned = isOwnAddition(selected);
  const needsReview = catalogEditsNeedReview() && !selectedOwned;
  // Labels picked for a product this customer didn't add; linked on approval.
  const [stagedLabels, setStagedLabels] = useState<FoodLabelLink[]>([]);

  // Only allow a save when the draft actually differs from the loaded record
  // (or, for reviewed edits, new labels were picked).
  const changed = useMemo(
    () =>
      foodsChanged(draft, selected) || (needsReview && stagedLabels.length > 0),
    [draft, selected, needsReview, stagedLabels]
  );

  const [reviewRunning, setReviewRunning] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("");
  const [reviewItem, setReviewItem] = useState<IngestReviewItem | null>(null);
  const [reviewError, setReviewError] = useState("");

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const staff = isCatalogStaff();

  const refreshPending = useCallback(async () => {
    try {
      const res = await api.listMyChangeRequests({
        status: "pending",
        pageSize: 100,
      });
      const map: Record<string, CatalogChangeRequest> = {};
      res.items.forEach((r) => {
        if (!map[r.foodId]) map[r.foodId] = r;
      });
      setPendingByFood(map);
    } catch {
      setPendingByFood({});
    }
  }, [api]);

  const load = useCallback(
    async (f: TabFilter, p: number) => {
      setLoading(true);
      setListError("");
      try {
        if (isRequestFilter(f)) {
          const res = await api.listMyChangeRequests({
            status: f,
            page: p,
            pageSize: PAGE_SIZE,
          });
          setItems([]);
          setRequests(res.items);
          setTotal(res.total);
          if (res.items.length === 0) setListError(EMPTY_MESSAGES[f]);
        } else {
          const res = await api.getMyContributions(f, p, PAGE_SIZE);
          setRequests([]);
          setItems(res.items);
          setTotal(res.total);
          if (res.items.length === 0) setListError(EMPTY_MESSAGES[f]);
        }
      } catch (err) {
        setItems([]);
        setRequests([]);
        setTotal(0);
        setListError(extractApiError(err, "Failed to load your products"));
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  useEffect(() => {
    void load(filter, page);
  }, [load, filter, page]);

  useEffect(() => {
    void refreshPending();
  }, [refreshPending]);

  // Batch-load any existing product/brand images for the current page of rows.
  useEffect(() => {
    if (items.length === 0) {
      setImageMap({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const ids = items.map((i) => i.id || "").filter(Boolean);
        const brands = Array.from(
          new Set(items.map((i) => i.brandName || "").filter(Boolean))
        );
        const res = await api.lookupImages({
          productIds: ids,
          brandNames: brands,
        });
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const it of items) {
          const id = it.id || "";
          const url =
            (id && res.products[id]) ||
            (it.brandName && res.brands[it.brandName]) ||
            "";
          if (id && url) map[id] = url;
        }
        setImageMap(map);
      } catch {
        if (!cancelled) setImageMap({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [items, api]);

  const changeFilter = (f: TabFilter) => {
    if (f === filter) return;
    setFilter(f);
    setPage(1);
  };

  const resetEditor = () => {
    setDirtySinceRecheck(false);
    setSaveMsg("");
    setSaveErr("");
    setReviewItem(null);
    setReviewError("");
    setStagedLabels([]);
  };

  const select = (f: PassioFood) => {
    setSelectedId(f.id || "");
    setSelected(f);
    setDraft(f);
    setHasBlocking(hasBlockingConcern(f.concerns));
    resetEditor();
  };

  // Open the live product for a review request. With `resubmit`, the editor
  // starts from the customer's own (rejected) proposal so they can fix it.
  const openRequest = async (r: CatalogChangeRequest, resubmit: boolean) => {
    resetEditor();
    setSelectedId(r.foodId);
    try {
      const live = await api.getFoodById(r.foodId);
      setSelected(live);
      const start =
        resubmit && r.proposed ? { ...r.proposed, id: live.id } : live;
      setDraft(start);
      setHasBlocking(hasBlockingConcern(start.concerns));
      if (resubmit) {
        setSaveMsg(
          "Your earlier proposal is loaded. Fix what the reviewer pointed out, then submit it again."
        );
      }
    } catch (err) {
      setSelected(null);
      setDraft(null);
      setSaveErr(extractApiError(err, "This product couldn't be loaded."));
    }
  };

  const withdraw = async (r: CatalogChangeRequest) => {
    setWithdrawing(r.id);
    try {
      await api.withdrawChangeRequest(r.id);
      await Promise.all([load(filter, page), refreshPending()]);
    } catch (err) {
      setListError(extractApiError(err, "Couldn't withdraw this request."));
    } finally {
      setWithdrawing("");
    }
  };

  const recheck = async () => {
    if (!draft) return;
    setRechecking(true);
    setSaveErr("");
    setSaveMsg("");
    try {
      const res = await api.recheckConcerns(draft);
      setDraft(res.food);
      setDirtySinceRecheck(false);
      const blocking = res.blocking ?? hasBlockingConcern(res.concern_codes);
      setHasBlocking(blocking);
      setSaveMsg(
        blocking
          ? "Re-checked — still has blocking concern codes; fix the flagged fields before saving."
          : "Re-checked — no blocking concerns. Good to save."
      );
    } catch (err) {
      setSaveErr(extractApiError(err, "Re-check failed"));
    } finally {
      setRechecking(false);
    }
  };

  const handleEditingChange = (isEditing: boolean) => {
    if (!isEditing && dirtySinceRecheck && !rechecking) void recheck();
  };

  // Shared tail of a save: live writes refresh the row; reviewed writes leave
  // the live record alone and show where to track the request.
  const afterWrite = async (write: CatalogWriteResult, liveMessage: string) => {
    if (!selected?.id) return;
    if (write.status === "pending_review") {
      setStagedLabels([]);
      setSaveMsg(REVIEW_MESSAGE);
      await refreshPending();
      return;
    }
    const result = write.food;
    await api.notifyChange({
      action: "update",
      food: result,
      original: selected,
      customerName,
    });
    setSelected(result);
    setDraft(result);
    setHasBlocking(false);
    setItems((list) => list.map((it) => (it.id === selected.id ? result : it)));
    setSaveMsg(liveMessage);
  };

  const save = async () => {
    if (!selected || !draft || !selected.id) return;
    if (!changed) {
      setSaveMsg("");
      setSaveErr("No changes to save.");
      return;
    }
    setSaving(true);
    setSaveMsg("");
    setSaveErr("");
    try {
      // Always re-run the concern-code sanity checks against the current edits
      // before persisting, and refuse to save if anything blocks (same rule the
      // Add Products review enforces before a write).
      const check = await api.recheckConcerns(draft);
      const validated = check.food;
      setDraft(validated);
      setDirtySinceRecheck(false);
      const blocking =
        check.blocking ?? hasBlockingConcern(check.concern_codes);
      setHasBlocking(blocking);
      if (blocking) {
        setSaveErr(
          "Can't save — this product still has blocking concern codes. Fix the flagged fields (highlighted in the editor), then save."
        );
        return;
      }
      const updated: PassioFood = { ...validated, id: selected.id };
      const write = await api.updateFood(
        updated,
        undefined,
        needsReview ? stagedLabels : undefined
      );
      await afterWrite(write, "Saved — your change is live in the catalog.");
    } catch (err) {
      setSaveErr(extractApiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const reReview = async () => {
    if (!selected) return;
    setReviewRunning(true);
    setReviewError("");
    setReviewItem(null);
    try {
      const resp = await api.runChain(
        ingestChainId,
        { food_input: JSON.stringify(selected) },
        {
          metadata: {
            feature: "contributions-re-review",
            food_id: selected.id,
            item_count: 1,
          },
          onStatus: (s) => setReviewStatus(s),
        }
      );
      const result = extractIngestResult(resp);
      setReviewItem(result.items[0] || null);
      if (!result.items[0])
        setReviewError("Re-research returned no proposed record.");
    } catch (err) {
      setReviewError(extractApiError(err, "Re-research failed"));
    } finally {
      setReviewRunning(false);
    }
  };

  const applyReReview = async () => {
    if (!selected?.id || !reviewItem) return;
    const proposed = (reviewItem.ingest_payload || reviewItem.proposed) as
      | PassioFood
      | undefined;
    if (!proposed) return;
    if (hasBlockingConcern(proposed.concerns)) {
      setSaveErr(
        "Can't apply — the re-researched record has blocking concern codes. Open it in the full review page to resolve them first."
      );
      return;
    }
    setSaving(true);
    setSaveErr("");
    setSaveMsg("");
    try {
      const updated: PassioFood = { ...proposed, id: selected.id };
      const write = await api.updateFood(updated);
      setReviewItem(null);
      await afterWrite(write, "Updated from re-research — live now.");
    } catch (err) {
      setSaveErr(extractApiError(err, "Apply failed"));
    } finally {
      setSaving(false);
    }
  };

  const rangeLabel = useMemo(() => {
    if (total === 0) return "";
    const shown = isRequestFilter(filter) ? requests.length : items.length;
    const start = (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(total, (page - 1) * PAGE_SIZE + shown);
    return `${start}–${end} of ${total}`;
  }, [total, page, items.length, requests.length, filter]);

  const selectedPending = selected?.id ? pendingByFood[selected.id] : undefined;
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* List column */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Your products &amp; contributions
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Everything you&apos;ve added or edited through the Catalog Manager.
          Products you added are yours to fix any time; edits to other products
          are checked by Passio before they go live.
        </p>

        <div className="mt-3 inline-flex flex-wrap rounded-md border border-gray-200 bg-gray-50 p-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => changeFilter(f.id)}
              title={f.hint}
              className={`rounded px-3 py-1.5 text-sm font-semibold transition ${
                filter === f.id
                  ? "bg-white text-indigo-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {f.label}
              {f.id === "pending" && Object.keys(pendingByFood).length > 0 ? (
                <span className="ml-1.5 rounded-full bg-indigo-100 px-1.5 text-xs text-indigo-700">
                  {Object.keys(pendingByFood).length}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
          <span>{loading ? "Loading…" : rangeLabel}</span>
          <div className="flex items-center gap-2">
            <SecondaryButton
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
            >
              Prev
            </SecondaryButton>
            <span>
              {page} / {totalPages}
            </span>
            <SecondaryButton
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              Next
            </SecondaryButton>
          </div>
        </div>

        {listError && (
          <div className="mt-2 text-sm text-gray-500">{listError}</div>
        )}

        <div className="mt-3 max-h-[32rem] space-y-2 overflow-auto pr-1">
          {loading && items.length === 0 && requests.length === 0 ? (
            <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
              <Spinner label="Loading your products…" />
            </div>
          ) : isRequestFilter(filter) ? (
            requests.map((r) => (
              <div
                key={r.id}
                className={`rounded-md border p-3 ${
                  selectedId === r.foodId
                    ? "border-indigo-400 bg-indigo-50"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-gray-900">
                      {r.foodName || "(unnamed)"}
                    </div>
                    <div className="text-xs text-gray-500">
                      Submitted {formatDate(r.created)}
                      {r.reviewedAt && r.status !== "pending"
                        ? ` · reviewed ${formatDate(r.reviewedAt)}`
                        : ""}
                    </div>
                  </div>
                  <StatusChip status={r.status} />
                </div>
                {r.status === "rejected" && r.rejectReason && (
                  <div className="mt-2 rounded border border-red-100 bg-red-50 px-2 py-1.5 text-xs text-red-800">
                    <span className="font-semibold">Reviewer note:</span>{" "}
                    {r.rejectReason}
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  {r.status === "pending" ? (
                    <>
                      <SecondaryButton
                        type="button"
                        onClick={() => void openRequest(r, false)}
                      >
                        View product
                      </SecondaryButton>
                      <SecondaryButton
                        type="button"
                        disabled={withdrawing === r.id}
                        onClick={() => void withdraw(r)}
                      >
                        {withdrawing === r.id ? "Withdrawing…" : "Withdraw"}
                      </SecondaryButton>
                    </>
                  ) : (
                    <SecondaryButton
                      type="button"
                      onClick={() => void openRequest(r, true)}
                    >
                      Edit and resubmit
                    </SecondaryButton>
                  )}
                </div>
              </div>
            ))
          ) : (
            items.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => select(f)}
                className={`block w-full rounded-md border p-3 text-left transition hover:border-indigo-300 ${
                  selectedId === f.id
                    ? "border-indigo-400 bg-indigo-50"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-start gap-3">
                  {imageMap[f.id || ""] && (
                    <img
                      src={imageMap[f.id || ""]}
                      alt=""
                      className="h-10 w-10 flex-shrink-0 rounded border border-gray-200 bg-white object-contain"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium text-gray-900">
                        {f.name || "(unnamed)"}
                      </span>
                      <span className="flex flex-shrink-0 items-center gap-1">
                        {f.id && pendingByFood[f.id] && (
                          <StatusChip status="pending" />
                        )}
                        <StatusChip
                          status={isOwnAddition(f) ? "added" : "updated"}
                        />
                        {f.concerns && f.concerns.length > 0 && (
                          <Badge tone="amber">
                            {f.concerns.length} concern
                            {f.concerns.length === 1 ? "" : "s"}
                          </Badge>
                        )}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {rowSubtitle(f)}
                    </div>
                    {f.tags && f.tags.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {f.tags.slice(0, 4).map((t) => (
                          <Badge key={t} tone="blue">
                            {t}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Editor column */}
      <div>
        {!selected || !draft ? (
          <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
            {saveErr ||
              "Pick one of your products to edit any field (incl. nutrients & portions), or re-research it against the latest online data."}
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit product
              </h3>
              {staff && (
                <span className="text-xs text-gray-400">id {selected.id}</span>
              )}
            </div>

            {selectedPending ? (
              <div className="mt-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
                ⏳ You have a change waiting for review (submitted{" "}
                {formatDate(selectedPending.created)}). Saving again replaces it
                with your newer version.
              </div>
            ) : needsReview ? (
              <div className="mt-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                You didn&apos;t add this product, so Passio reviews your changes
                and new labels before they go live. The current version stays
                visible until then. You can add a photo if it doesn&apos;t have
                one, but not replace an existing photo.
              </div>
            ) : null}

            <div className="mt-3 rounded-md border border-gray-100 bg-gray-50 p-3">
              <FoodDetailPanel
                food={draft}
                onChange={(next) => {
                  setDraft(next);
                  setDirtySinceRecheck(true);
                }}
                onEditingChange={handleEditingChange}
                onRecheck={recheck}
                rechecking={rechecking}
                labelSlot={
                  selected.id ? (
                    <LabelLink
                      api={api}
                      foodId={selected.id}
                      suggestions={labelSuggestions}
                      stageForReview={needsReview}
                      value={stagedLabels}
                      onChange={setStagedLabels}
                    />
                  ) : undefined
                }
              />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <PrimaryButton
                type="button"
                onClick={save}
                disabled={saving || rechecking || !changed || hasBlocking}
                title={
                  !changed
                    ? "No changes to save"
                    : hasBlocking
                    ? "Fix the blocking concern codes before saving"
                    : undefined
                }
              >
                {saving
                  ? "Saving…"
                  : needsReview
                  ? "Submit for review"
                  : "Save changes"}
              </PrimaryButton>
              <SecondaryButton
                type="button"
                onClick={recheck}
                disabled={rechecking || saving || !draft}
                title="Re-run the concern-code sanity checks against your edits"
              >
                {rechecking ? "Validating…" : "Validate"}
              </SecondaryButton>
              <SecondaryButton
                type="button"
                onClick={reReview}
                disabled={reviewRunning || !ingestChainId}
                title="Re-runs the AI research on this product against the latest online sources and proposes a refreshed record you can apply. Uses tokens from your balance."
              >
                {reviewRunning ? "Re-researching…" : "Re-research online"}
              </SecondaryButton>
              {reviewRunning && (
                <Spinner
                  label={
                    reviewStatus === "running" ? "Researching…" : "Starting…"
                  }
                />
              )}
            </div>
            {!changed ? (
              <p className="mt-1.5 text-xs text-gray-400">
                Edit a field to enable saving.
              </p>
            ) : hasBlocking ? (
              <p className="mt-1.5 text-xs font-medium text-red-600">
                ⚠ Blocking concern codes must be cleared before this can be
                saved. Fix the highlighted fields, then Validate.
              </p>
            ) : null}

            {saveMsg && (
              <div className="mt-2 text-sm text-green-700">{saveMsg}</div>
            )}
            {saveErr && (
              <div className="mt-2 text-sm text-red-600">{saveErr}</div>
            )}
            {reviewError && (
              <div className="mt-2 text-sm text-red-600">{reviewError}</div>
            )}

            {reviewItem && (
              <div className="mt-4 rounded-md border border-indigo-200 bg-indigo-50 p-3">
                <div className="text-sm font-semibold text-indigo-900">
                  Re-research proposal
                </div>
                <p className="text-xs text-indigo-700">
                  Latest online data, formatted for the database. Review and
                  apply if it&apos;s an improvement
                  {needsReview
                    ? " (it goes to Passio for review first)."
                    : " (updates this item in place)."}
                </p>
                <div className="mt-2 rounded-md border border-gray-100 bg-white p-3">
                  <FoodDetailPanel
                    food={
                      (reviewItem.ingest_payload ||
                        reviewItem.proposed) as PassioFood
                    }
                  />
                </div>
                <PrimaryButton
                  type="button"
                  className="mt-2"
                  onClick={applyReReview}
                  disabled={saving}
                >
                  {needsReview
                    ? "Submit re-research for review"
                    : "Apply update from re-research"}
                </PrimaryButton>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContributionsTab;
